/**
 * An opened item's ⋯ and right-click (opened-window rules 6 and 7, frame
 * menu audit 2026-10-27): the shared frame menu for an opened item, led by
 * the right-clicked selection and the type's extras. Every opened surface
 * draws these: focus windows, floating windows and the docked chat; ⋯ is
 * left out when the menu has nothing (`item-context-menu.ts` lets the
 * right-click through then).
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { Button, Icons, MenuSurfaceContext } from "@/components/ui";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import type { FrameChromeMenuItem } from "@/components/shapes/shared/frame-chrome-config";
import type {
  FrameItemContextMenuProps,
  FrameItemMenuProps,
} from "@/components/shapes/shared/frame-renderer-context";
import {
  frameMenuFacts,
  openedMenuOpens,
} from "@/components/wall/card-menu-items";
import { WallCardMenuHost } from "@/components/wall/wall-card-menu-host";
import { useWallFrame } from "@/components/wall/wall-frame-source";
import { useT } from "@/i18n";
import { focusMessages } from "@/i18n/messages/focus";
import type { FrameData } from "@/lib/frame-data";

/**
 * Whether ⋯ has anything, from the same facts as the right-click
 * (`item-context-menu.ts`). An item that is not a Project tool always has
 * Archive (or Unarchive) and Delete; a tool has its extras, Copy and
 * Download. The frame is the wall's, else the one the window shows (a
 * fresh docked chat).
 */
function useHasMenuItems(
  shownFrame: FrameData,
  extras: readonly FrameChromeMenuItem[],
): boolean {
  const frame = useWallFrame(shownFrame.frameId) ?? shownFrame;
  return useMemo(
    () =>
      openedMenuOpens(frame, frameMenuFacts(frame.frameId), {
        extras,
        selection: "",
      }),
    [extras, frame],
  );
}

export function ItemMenuButton({ frame, extras }: FrameItemMenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<ContextMenuAnchor | null>(null);
  const frameId = frame.frameId;
  const hasItems = useHasMenuItems(frame, extras);
  const t = useT(focusMessages);
  const target = useMemo(
    () => (anchor ? { frameId, anchor } : null),
    [anchor, frameId],
  );
  // Stable, so the menu reads the frame's content only when it changes.
  const frameRoot = useCallback(
    () =>
      buttonRef.current?.closest<HTMLElement>("[data-canvas-frame-id]") ?? null,
    [],
  );
  if (!hasItems) return null;
  return (
    <>
      <Button
        ref={buttonRef}
        type="button"
        variant="ghost"
        size="iconSm"
        aria-label={t("moreActions")}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        title={t("moreActions")}
        data-testid={`focus-window-menu-${frameId}`}
        onClick={(event) => {
          // Under the button, its right edge lined up with the button's;
          // above it near the window's bottom.
          const rect = event.currentTarget.getBoundingClientRect();
          setAnchor({ rect, align: "end" });
        }}
      >
        <Icons.MoreHorizontal className="h-3.5 w-3.5" />
      </Button>
      {/* The opened window wears its card's paper, so its menu does too. */}
      <MenuSurfaceContext.Provider value="card">
        <WallCardMenuHost
          target={target}
          surface="opened"
          onClose={() => setAnchor(null)}
          extras={extras}
          shownFrame={frame}
          frameRoot={frameRoot}
        />
      </MenuSurfaceContext.Provider>
    </>
  );
}

/** The same menu on a right-click, led by Copy selection. */
export function ItemContextMenu({
  frame,
  extras,
  anchor,
  selection,
  frameRoot,
  onClose,
}: FrameItemContextMenuProps) {
  const frameId = frame.frameId;
  const target = useMemo(
    () => (anchor ? { frameId, anchor } : null),
    [anchor, frameId],
  );
  return (
    <MenuSurfaceContext.Provider value="card">
      <WallCardMenuHost
        target={target}
        surface="opened"
        onClose={onClose}
        extras={extras}
        selection={selection}
        shownFrame={frame}
        frameRoot={frameRoot}
      />
    </MenuSurfaceContext.Provider>
  );
}
